import { parseJsonSafely } from '../helpers.js';
import {
    ACCOUNT_IMPORT_INVALID_REASON,
    ACCOUNT_IMPORT_RESPONSE_STATUS,
    ACCOUNT_IMPORT_RESULT_KIND,
} from './constants.js';

export async function submitAccountImportValidation(fetchImpl, { csrfToken, file, form, validateUrl }) {
    const response = await fetchImpl(validateUrl, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken,
        },
        body: createArchiveFormData(form, file),
    });

    const payload = await parseJsonSafely(response);

    if (response.ok && payload.success === true && payload.status === ACCOUNT_IMPORT_RESPONSE_STATUS.VALID) {
        const summary = normalizeSummary(payload.summary);
        const schemaVersion = normalizeSchemaVersion(payload.schema_version);

        if (summary !== null && schemaVersion !== null) {
            return {
                kind: ACCOUNT_IMPORT_RESULT_KIND.VALID,
                schemaVersion,
                summary,
            };
        }
    }

    if (response.status === 422 && payload.status === ACCOUNT_IMPORT_RESPONSE_STATUS.INVALID) {
        return {
            kind: ACCOUNT_IMPORT_RESULT_KIND.INVALID,
            message: mapInvalidImportMessage(payload.reason),
            reason: typeof payload.reason === 'string' ? payload.reason.trim() : '',
        };
    }

    return {
        kind: ACCOUNT_IMPORT_RESULT_KIND.FAILED,
        message: response.status >= 500
            ? 'Import validation is temporarily unavailable. Please try again later.'
            : 'Import validation could not be completed. Please try another archive.',
        tone: response.status >= 500 ? 'error' : 'warning',
    };
}

export async function submitAccountImportExecution(fetchImpl, { csrfToken, executeUrl, file, form }) {
    const response = await fetchImpl(executeUrl, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken,
        },
        body: createArchiveFormData(form, file),
    });

    const payload = await parseJsonSafely(response);

    if (response.ok && payload.success === true && payload.status === ACCOUNT_IMPORT_RESPONSE_STATUS.IMPORTED) {
        return {
            kind: ACCOUNT_IMPORT_RESULT_KIND.IMPORTED,
        };
    }

    if (response.status === 422 && payload.status === ACCOUNT_IMPORT_RESPONSE_STATUS.INVALID) {
        return {
            kind: ACCOUNT_IMPORT_RESULT_KIND.INVALID,
            message: mapInvalidImportMessage(payload.reason),
            reason: typeof payload.reason === 'string' ? payload.reason.trim() : '',
        };
    }

    return {
        kind: ACCOUNT_IMPORT_RESULT_KIND.FAILED,
        message: normalizeExecutionFailureMessage(response.status, payload.error),
        tone: response.status >= 500 ? 'error' : 'warning',
    };
}

function normalizeSummary(summary) {
    if (typeof summary !== 'object' || summary === null) {
        return null;
    }

    const normalized = {
        games: normalizeCount(summary.games),
        manual_lists: normalizeCount(summary.manual_lists),
        rankings: normalizeCount(summary.rankings),
        notes: normalizeCount(summary.notes),
        note_attachments: normalizeCount(summary.note_attachments),
        dictionary_statuses: normalizeCount(summary.dictionary_statuses),
        dictionary_hardware: normalizeCount(summary.dictionary_hardware),
        dictionary_platforms: normalizeCount(summary.dictionary_platforms),
        dictionary_tags: normalizeCount(summary.dictionary_tags),
    };

    return Object.values(normalized).every((value) => value !== null) ? normalized : null;
}

function normalizeCount(value) {
    return Number.isInteger(value) && value >= 0 ? value : null;
}

function normalizeSchemaVersion(value) {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim();
    if (normalized === '') {
        return null;
    }

    const versionAliasMatch = normalized.match(/^v(\d+)$/i);
    if (versionAliasMatch !== null) {
        return `v${versionAliasMatch[1]}`;
    }

    const semanticVersionMatch = normalized.match(/^(\d+)(?:\.\d+){0,2}$/);
    if (semanticVersionMatch !== null) {
        return `v${semanticVersionMatch[1]}`;
    }

    return null;
}

function createArchiveFormData(form, file) {
    const formData = new FormData(form);
    if (file !== null) {
        formData.set('archive', file);
    }

    return formData;
}

function mapInvalidImportMessage(reason) {
    switch (reason) {
        case ACCOUNT_IMPORT_INVALID_REASON.MISSING_ARCHIVE_UPLOAD:
            return 'Choose a ZIP archive to continue.';
        case ACCOUNT_IMPORT_INVALID_REASON.INVALID_ZIP_ARCHIVE:
            return 'This file cannot be restored. Choose a ZIP exported from Casualer with Export data above, up to 64 MiB.';
        case ACCOUNT_IMPORT_INVALID_REASON.ARCHIVE_UPLOAD_TOO_LARGE:
            return 'The import ZIP exceeds the 64 MiB upload limit.';
        case ACCOUNT_IMPORT_INVALID_REASON.ARCHIVE_ATTACHMENT_ENTRY_TOO_LARGE:
        case ACCOUNT_IMPORT_INVALID_REASON.ARCHIVE_ATTACHMENT_TOTAL_TOO_LARGE:
            return 'Archive attachments exceed the current import size limit.';
        case ACCOUNT_IMPORT_INVALID_REASON.UNSAFE_ARCHIVE_ENTRY_PATH:
        case ACCOUNT_IMPORT_INVALID_REASON.INVALID_GAME_ARCHIVE_PATH:
            return 'Archive structure is not supported for import.';
        default:
            return 'Import archive is invalid.';
    }
}

function normalizeExecutionFailureMessage(status, error) {
    if (typeof error === 'string' && error.trim() !== '') {
        return error.trim();
    }

    return status >= 500
        ? 'Import is temporarily unavailable. Please try again later.'
        : 'Import could not be completed. Please try another archive.';
}
